import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "@/components/ui/sonner";
import ToneGenerator from "@/components/ToneGenerator";
import BrainLattice from "@/components/BrainLattice";
import CamelotWheel from "@/components/CamelotWheel";
import ParticleLab from "@/components/ParticleLab";
import SoundLab from "@/components/SoundLab";
import FrequencyVisualModal from "@/components/FrequencyVisualModal";
import FrequencyGiftModal from "@/components/FrequencyGiftModal";
import { stopAll } from "@/lib/audioEngine";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

export default function Dashboard() {
  const navigate = useNavigate();
  const [insights, setInsights] = useState(null);
  const [sessionLeft, setSessionLeft] = useState(0);
  const [sessionRunning, setSessionRunning] = useState(false);
  const timerRef = useRef(null);
  const [visualModalOpen, setVisualModalOpen] = useState(false);
  const [selectedVisualFreq, setSelectedVisualFreq] = useState(null);
  const [giftModalOpen, setGiftModalOpen] = useState(false);
  const [selectedGiftFreq, setSelectedGiftFreq] = useState(null);

  const load = async () => {
    try {
      const i = await axios.get(`${API}/insights`).catch(() => ({ data: null }));
      setInsights(i.data);
    } catch {}
  };

  const clearTimer = () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };
  const openArchive = () => navigate('/archive');

  useEffect(() => { load(); return () => clearTimer(); }, []);

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-8 space-y-8" data-testid="dashboard-page">

      {/* Hero — Artist-led positioning */}
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="gm-display text-4xl sm:text-5xl text-[#FF0000]" data-testid="app-title">MIRRORS</h1>
            <p className="text-[11px] font-mono text-[#FF0000]/40 tracking-[0.28em] uppercase mt-1">Functional sound · Vince Sinnott</p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="gm-chip"
                data-testid="library-dropdown-trigger"
              >
                LIBRARY
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-48 bg-black/95 border border-[#FF0000]/30 text-[#FF0000] font-mono text-xs tracking-widest uppercase"
              data-testid="library-dropdown-menu"
            >
              <DropdownMenuItem onSelect={openArchive} data-testid="library-open-archive">
                Open Archive
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => {
                  stopAll();
                  clearTimer();
                }}
                data-testid="library-stop"
              >
                Stop Playback
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        </div>
      </div>

      {/* Sound Lab — Discography first */}
      <SoundLab />

      {/* ParticleLab */}
      <ParticleLab onBrowseArchive={openArchive} />

      {/* Brain Lattice — Visual frequency map on top */}
      <BrainLattice />

      {/* Frequency Terminal — T2 Liquid Metal */}
      <ToneGenerator />

      {/* Interactive Mirrors Wheel — directly below Terminal */}
      <CamelotWheel />

      <FrequencyVisualModal 
        frequencyHz={selectedVisualFreq}
        isOpen={visualModalOpen}
        onClose={() => setVisualModalOpen(false)}
      />
      
      <FrequencyGiftModal 
        frequencyHz={selectedGiftFreq}
        isOpen={giftModalOpen}
        onClose={() => setGiftModalOpen(false)}
      />
    </div>
  );
}
